import React from "react";
import { Link } from "react-router-dom";
import { colors, fonts, stripe, eyebrow } from "./theme";
import { contact } from "./data";
import { phoneWaHref } from "./whatsapp";

// Small uppercase mono label used above headings.
export function Eyebrow({ children, style }) {
  return <span style={{ ...eyebrow, ...style }}>{children}</span>;
}

// Phone number rendered as a WhatsApp deep link. Every call site used to point
// a bare tel: at this number; enquiries now go through WhatsApp instead, so
// the visible digits are unchanged but tapping them opens a chat rather than
// dialling. aria-label carries that distinction to screen reader users, who
// otherwise have no way to tell the target changed from the digits alone.
export function PhoneLink({ children, style, className }) {
  return (
    <a
      href={phoneWaHref}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      style={style}
      aria-label={`${contact.phone} — opens WhatsApp`}
    >
      {children || contact.phone}
    </a>
  );
}

// Postal address rendered as a Google Maps deep link, wrapping whatever
// markup the caller already has (line breaks included) so it keeps reading as
// an address rather than a button.
export function AddressLink({ children, style, className }) {
  return (
    <a
      href={contact.mapLink}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      style={style}
      aria-label="Sigma Enterprises location — opens in Google Maps"
    >
      {children}
    </a>
  );
}

// Shared form-field styling (used by the contact form and the enquiry modal).
export const fieldLabelStyle = {
  fontFamily: fonts.mono,
  fontSize: "var(--fs-label)",
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  // muted2 (#8A8175) measures 3.83:1 on white — under the 4.5:1 floor at this
  // size. muted (#6F675B) is 5.57:1 and keeps the same grey cast.
  color: colors.muted,
};

// Shared across the contact form and the product enquiry modal.
//
// fontSize is pinned at 16px and must not go below it: Safari on iOS zooms the
// whole viewport in whenever a focused input is under 16px, and since the page
// (correctly) does not set maximum-scale there is nothing to zoom back out —
// the reader is left part-way through a form on a layout that now scrolls
// sideways. minHeight carries the control to the 44px touch floor, which
// 11px of vertical padding on a 14px line box missed by 3px.
export const inputStyle = {
  width: "100%",
  minHeight: "44px",
  padding: "12px 14px",
  border: `1px solid ${colors.inputBorder}`,
  borderRadius: "2px",
  background: colors.inputBg,
  fontSize: "16px",
  lineHeight: 1.5,
  color: colors.ink,
  marginTop: "8px",
};

// Diagonally-striped image placeholder with a centered chip label.
export function Placeholder({ label, ratio = "4/5", tight = false, style, className }) {
  return (
    <div
      className={className}
      style={{
        aspectRatio: ratio,
        border: `1px solid ${colors.border}`,
        borderRadius: "4px",
        background: stripe(tight),
        display: "grid",
        placeItems: "center",
        ...style,
      }}
    >
      <span
        style={{
          fontFamily: fonts.mono,
          fontSize: "var(--fs-label)",
          letterSpacing: "0.1em",
          color: colors.brown,
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          padding: "7px 12px",
          borderRadius: "2px",
        }}
      >
        {label}
      </span>
    </div>
  );
}

// Nominal pixel box for a given card ratio. These become the <img> width/height
// attributes: the browser derives an aspect ratio from them and reserves the
// right space before a single byte of the image arrives, so nothing below the
// card moves when it loads. They deliberately describe the *rendered* box
// rather than the file's true intrinsic size — the photo is object-fit: cover,
// so its own aspect ratio is not what the layout is built from.
const RATIO_BOX = {
  "4/3": { width: 640, height: 480 },
  "16/9": { width: 640, height: 360 },
  "4/5": { width: 640, height: 800 },
  "1/1": { width: 640, height: 640 },
};

function boxFor(ratio) {
  if (RATIO_BOX[ratio]) return RATIO_BOX[ratio];
  const [w, h] = String(ratio).split("/").map(Number);
  if (w > 0 && h > 0) return { width: 640, height: Math.round((640 * h) / w) };
  return RATIO_BOX["4/3"];
}

// Product media area: shows a real photo when `src` is set (and loads), otherwise
// falls back to the diagonally-striped placeholder. The `tag` chip stays visible
// either way — over the image as a corner badge, or centered on the placeholder.
//
// `priority` marks a card that is above the fold. Those images are the Largest
// Contentful Paint candidate on their page, and loading="lazy" actively hurts
// there: the browser defers the request until layout has run, pushing LCP out
// by hundreds of milliseconds for an image that was always going to be visible.
// Priority images load eagerly at high fetch priority; everything else stays
// lazy so off-screen cards cost nothing.
//
// `fetchpriority` is spelled lowercase deliberately. react-dom 18.2 does not
// know this attribute — grep the package and there are zero occurrences — so
// the camelCase form logs "React does not recognize the fetchPriority prop" on
// every single render. React still emits it, lowercased, so the hint was
// always reaching the HTML; the cost was console noise burying real warnings.
// Lowercase passes through as a plain custom attribute with identical output
// and no warning. On a React 19 upgrade, switch back to `fetchPriority` — 19
// knows the prop, and then it is the lowercase form that warns.
// Every image that reaches this component comes from the `card` profile in
// scripts/optimize-images.js, which emits a 640w master and a 400w companion
// named <name>-400.webp. The guard below skips the companion for anything that
// already carries a width suffix, so the hero (hero-1920.webp) could never
// have a "-400" invented for it, and the two `square` images — stock/stock2,
// used by the About photo — are rendered by a plain <img>, not by this.
function cardSrcSet(src) {
  if (!src || !src.endsWith(".webp") || /-\d+\.webp$/.test(src)) return undefined;
  return `${src.replace(/\.webp$/, "-400.webp")} 400w, ${src} 640w`;
}

export function ProductImage({ src, alt, tag, ratio = "4/3", tight = true, priority = false }) {
  const [failed, setFailed] = React.useState(false);
  const hasImg = Boolean(src) && !failed;
  const box = boxFor(ratio);
  const srcSet = cardSrcSet(src);
  return (
    <div
      style={{
        position: "relative",
        aspectRatio: ratio,
        background: stripe(tight),
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
      }}
    >
      {hasImg && (
        <img
          src={src}
          srcSet={srcSet}
          // These cards render between ~210 and ~400 CSS px. `sizes` is
          // deliberately generous — it describes the widest a card gets at
          // each breakpoint, so the browser rounds up to the 640w master
          // rather than down to a 400w that would look soft. The 400w only
          // wins where it genuinely suffices: 1x displays, which is most
          // desktop traffic.
          sizes={srcSet ? "(max-width: 860px) calc(100vw - 40px), 320px" : undefined}
          alt={alt || ""}
          width={box.width}
          height={box.height}
          loading={priority ? "eager" : "lazy"}
          // Lowercase on purpose — see the note above the component.
          fetchpriority={priority ? "high" : "auto"}
          decoding={priority ? "sync" : "async"}
          onError={() => setFailed(true)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
        />
      )}
      {tag && (
        <span
          style={{
            position: hasImg ? "absolute" : "static",
            ...(hasImg ? { top: "8px", left: "8px" } : {}),
            zIndex: 1,
            fontFamily: fonts.mono,
            fontSize: "var(--fs-tag)",
            letterSpacing: "0.1em",
            color: colors.brown,
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            padding: "6px 10px",
            borderRadius: "2px",
          }}
        >
          {tag}
        </span>
      )}
    </div>
  );
}

// Button / link. variant: "dark" | "ghost" | "light".
// Pass `to` for in-app router navigation, `href` for external links, or `onClick`.
//
// Size is fluid rather than fixed: padding and label both interpolate between
// their 320px and 1280px ends via the --btn-* tokens in index.css. The 44px
// touch floor is held by minHeight rather than by the padding, because a
// caller that overrides `padding` (several do, to tighten a dense row) would
// otherwise silently drop the button under the minimum — which is exactly what
// had happened to the catalogue and brands CTAs at 42.9px.
export function Btn({ variant = "dark", to, onClick, href, children, style }) {
  const cls =
    variant === "dark" ? "se-btn-dark" : variant === "light" ? "se-btn-light" : "se-btn-ghost";
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "44px",
    padding: "var(--btn-py) var(--btn-px)",
    borderRadius: "2px",
    fontSize: "var(--btn-fs)",
    lineHeight: 1.2,
    fontWeight: 500,
    cursor: "pointer",
    ...style,
  };
  if (to) {
    return (
      <Link className={cls} to={to} style={base}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a className={cls} href={href} style={base}>
        {children}
      </a>
    );
  }
  // No `to` and no `href` means this is an action, not a link — the enquiry
  // modal openers all take this branch. It used to render <a onClick> with no
  // href, which is invisible to the keyboard (not focusable, no Enter/Space
  // activation) and announced as plain text. A real <button> costs nothing
  // visually once font and border are inherited from the shared class.
  // No `font: inherit` here, deliberately. React serialises a style object in
  // key order, and re-assigning a key that `...base` already set leaves it at
  // its original position — so the `font` shorthand always landed *after*
  // fontSize, fontWeight and lineHeight and reset all three to the inherited
  // values. The button rendered a weight lighter and a size off from every
  // other button on the page. The font family a <button> needs is already
  // handled globally by `input, textarea, select, button { font-family:
  // inherit }` in index.css, so nothing has to be restated here.
  return (
    <button type="button" className={cls} onClick={onClick} style={{ ...base, textAlign: "center" }}>
      {children}
    </button>
  );
}

export function Stat({ num, label }) {
  return (
    <div>
      <div style={{ fontFamily: fonts.serif, fontSize: "var(--fs-stat)", fontWeight: 600 }}>{num}</div>
      <div style={{ fontSize: "var(--fs-meta)", color: colors.muted, marginTop: "3px", lineHeight: 1.4 }}>{label}</div>
    </div>
  );
}

// Card linking to a category page, used in the Home + Catalogue grids.
// `priority` is forwarded to the photo — see ProductImage. Set it only on the
// cards that are genuinely above the fold on the page doing the rendering.
export function CategoryCard({ cat, to, priority = false }) {
  return (
    <Link
      className="se-card"
      to={to}
      style={{
        cursor: "pointer",
        border: `1px solid ${colors.border}`,
        borderRadius: "4px",
        background: colors.white,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ borderBottom: `1px solid ${colors.border}` }}>
        <ProductImage
          src={cat.image}
          alt={cat.imageAlt || cat.name}
          tag={`${cat.count} grades`}
          ratio="16/9"
          priority={priority}
        />
      </div>
      <div style={{ padding: "clamp(14px,3.5vw,16px) clamp(14px,4vw,18px) clamp(16px,4vw,18px)" }}>
        <div style={{ fontSize: "var(--fs-body)", fontWeight: 600 }}>{cat.name}</div>
        <div style={{ fontSize: "var(--fs-base)", color: colors.muted, lineHeight: 1.55, marginTop: "6px" }}>
          {cat.blurb}
        </div>
      </div>
    </Link>
  );
}

// A plain capability card. Replaces the previous ReviewCard, which rendered a
// five-star rating and an attributed quote — presentation that only belongs
// around a real, verifiable review. These cards make a claim in the site's own
// voice and are never marked up as Review schema.
export function AssuranceCard({ item }) {
  return (
    <div
      style={{
        border: `1px solid ${colors.border}`,
        borderRadius: "4px",
        background: colors.white,
        padding: "var(--sp-card) clamp(1.125rem,4.5vw,1.625rem)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h3
        style={{
          fontFamily: fonts.serif,
          fontSize: "var(--fs-h4)",
          fontWeight: 600,
          color: colors.ink,
          margin: "0 0 12px",
        }}
      >
        {item.t}
      </h3>
      <p
        style={{
          fontSize: "var(--fs-base)",
          lineHeight: 1.6,
          color: colors.muted,
          margin: 0,
        }}
      >
        {item.d}
      </p>
    </div>
  );
}

// The 4-up values band, shared by Home and About.
export function ValuesGrid({ values, style }) {
  return (
    <div
      style={{
        display: "grid",
        // min(240px,100%) stops the track floor from ever exceeding the
        // container: a bare minmax(240px,1fr) keeps its 240px minimum even in
        // a 204px column and pushes the document sideways. auto-fit rather
        // than auto-fill so a short list does not leave empty tracks.
        gridTemplateColumns: "repeat(auto-fit,minmax(min(240px,100%),1fr))",
        gap: "1px",
        background: colors.border,
        border: `1px solid ${colors.border}`,
        borderRadius: "4px",
        overflow: "hidden",
        ...style,
      }}
    >
      {values.map((v) => (
        <div key={v.t} style={{ background: colors.bg, padding: "var(--sp-card) clamp(1rem,4.5vw,1.375rem)" }}>
          <div style={{ fontFamily: fonts.serif, fontSize: "clamp(1.0625rem,0.9792rem + 0.4167vw,1.1875rem)", fontWeight: 600 }}>{v.t}</div>
          <div style={{ fontSize: "var(--fs-base)", color: colors.muted, lineHeight: 1.55, marginTop: "8px" }}>
            {v.d}
          </div>
        </div>
      ))}
    </div>
  );
}

// Section heading with a serif title and mono eyebrow, used across views.
export function SectionHead({ eyebrow: eb, title, style }) {
  return (
    <div style={style}>
      <Eyebrow>{eb}</Eyebrow>
      <h2 style={{ fontFamily: fonts.serif, fontSize: "var(--fs-h2)", fontWeight: 500, margin: "10px 0 0", lineHeight: 1.15 }}>
        {title}
      </h2>
    </div>
  );
}

export const assuranceGrid = {
  display: "grid",
  // See the note on ValuesGrid: the bare 300px floor overflowed a 264px
  // column by 36px at 320px, which pushed the whole page sideways.
  gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
  gap: "clamp(14px,3.5vw,18px)",
};
